<template>
    <div v-if="item" class="view--scroll view-spot">
        <header class="header">
            <a class="back" @click="$router.back()">&lt;</a>
            <h1 class="header__name">{{item.name}}</h1>
        </header>
        <article class="contents container">
            <section class="images">
                <swiper :options="$options.swiper">
                    <swiper-slide v-for="image in item.images" :key="image">
                        <img class="image" :src="image">
                    </swiper-slide>
                </swiper>
                <div class="images__dots" slot="pagination"></div>
            </section>
            <section class="titles">
                <p class="type">{{item.type}}</p>
                <h1 class="name lined"><span class="mask name__mask">{{item.name}}</span></h1>
                <h2 class="oneliner">{{item.oneliner}}</h2>
                <div v-if="hasCoupon" class="titles__coupon">
                    <img class="titles__coupon-icon" src="~@/assets/icon-mapcard-coupon.svg">
                    <div v-if="couponDetail" class="titles__coupon-text" v-html="couponDetail"></div>
                    <p v-else-if="item.couponCond || item.couponDiscount">
                        <span class="titles__coupon-cond">{{item.couponCond}}</span>
                        <span class="titles__coupon-discount">{{item.couponDiscount}}</span>
                    </p>
                </div>
            </section>
            <section class="body row">
                <div class="col col-12 col640-6">
                    <div>
                        <h1 class="body__heading"><span class="mask">공간 소개</span></h1>
                        <p class="body__text">{{item.description}}</p>
                    </div>
                    <div class="body__mobile">
                        <div v-if="item.openings">
                            <h1 class="body__heading">영업 시간</h1>
                            <p class="body__text">{{item.openings}}</p>
                        </div>
                        <div v-if="item.contact">
                            <h1 class="body__heading">문의</h1>
                            <p class="body__text">{{item.contact}}</p>
                        </div>
                        <div v-if="item.homepage">
                            <h1 class="body__heading">홈페이지</h1>
                            <p class="body__text"><a :href="item.homepage | httpfy" target="_blank">{{item.homepage}}</a></p>
                        </div>
                    </div>
                    <div>
                        <h1 class="body__heading"><span class="mask">위치</span></h1>
                        <p class="body__text">{{item.address}}</p>
                        <div class="map">
                            <a :href="mapHref" target="_blank">
                                <div class="map__body" ref="map" id="map"></div>
                                <img class="map__pin" src="http://static.naver.net/maps/mantle/2x/marker-default.png">
                            </a>
                            <p class="map__caption">*지도를 클릭하면 네이버 지도로 연결됩니다.</p>
                        </div>
                    </div>
                </div>
                <div class="col col-12 col640-6">
                    <div class="body__desktop">
                        <div v-if="item.openings">
                            <h1 class="body__heading"><span class="mask">영업 시간</span></h1>
                            <p class="body__text">{{item.openings}}</p>
                        </div>
                        <div v-if="item.contact">
                            <h1 class="body__heading"><span class="mask">문의</span></h1>
                            <p class="body__text">{{item.contact}}</p>
                        </div>
                        <div v-if="item.homepage">
                            <h1 class="body__heading"><span class="mask">홈페이지</span></h1>
                            <p class="body__text"><a :href="item.homepage | httpfy" target="_blank">{{item.homepage}}</a></p>
                        </div>
                    </div>
                </div>
            </section>
            <section v-if="hasCoupon" class="coupon">
                <section-title>COUPON</section-title>
                <h2 class="coupon__name">{{item.name}}</h2>
                <div class="coupon__text">
                    <div v-if="couponDetail" v-html="couponDetail"></div>
                    <div v-else-if="item.couponCond || item.couponDiscount">
                        <span v-if="item.couponCond" class="coupon__cond">{{item.couponCond}}</span>
                        <span v-if="item.couponDiscount" class="coupon__discount">{{item.couponDiscount}}</span>
                    </div>
                </div>
                <img class="coupon__image" :src="item.couponImage">
                <p class="coupon__image-caption">* 위 쿠폰 이미지는 샘플입니다.<br>현장에서 실제 쿠폰북을 받아보세요.</p>
            </section>
            <spot-programs v-if="item.programs.length" :spot="item"/>
            <v-track :title="item.title"/>
        </article>
        <v-track :title="item.title"/>
    </div>
</template>

<script>
import 'swiper/dist/css/swiper.css'

import Markdown from 'markdown-it'
import progress from 'nprogress'
import { swiper, swiperSlide } from 'vue-awesome-swiper/src/index'
import { db } from '@/core/shared'
import ProgramCard from '@/components/ProgramCard'
import { meta } from '@/mixins/meta'
import SpotPrograms from './SpotPrograms'

const markdown = new Markdown({ breaks: true })

const fetchItem = async (id) => {
    progress.start()
    const spotRef = db.collection('spots').doc(id)
    const spotAsync = spotRef.get()
    const programsAsync =
        db.collection('programs')
            .where('linkWith', '==', spotRef)
            .orderBy('date')
            .orderBy('timeStart').get()
    const spot = await spotAsync
    const programs = await programsAsync
    progress.done()
    if (! spot.exists) {
        return false
    }

    const data = { ...spot.data(), programs: [] }
    programs.forEach(x => {
        data.programs.push({ id: x.id, ...x.data() })
    })
    return data
}

export default {
    name: 'SpotView',
    swiper: {
        autoHeight: true,
        pagination: {
            el: '.images__dots'
        }
    },
    mixins: [meta],
    components: {
        swiper,
        swiperSlide,
        ProgramCard,
        SpotPrograms
    },
    data() {
        return {
            map: null,
            marker: null,
        }
    },
    computed: {
        hasCoupon() {
            return this.item.couponCond || this.item.couponDiscount || this.item.couponDetail
        },
        couponDetail() {
            return this.item.couponDetail ? markdown.render(this.item.couponDetail) : null
        },
        mapHref() {
            return `https://map.naver.com/?query=${window.encodeURIComponent(this.item.address)}`
        }
    },
    metaInfo() {
        return this.item ? {
            title: this.title,
            meta: [
                this.ogTitle,
                this.ogUrl,
                { name: 'og:description', content: this.item.oneliner },
                { name: 'og:image', content: this.item.images[0] },
                { name: 'og:image_alt', content: this.item.name }
            ]
        } : null
    },
    methods: {
        initMap(options) {
            this.map = new naver.maps.Map('map', options)
        }
    },
    async beforeRouteEnter(to, from, next) {
        const spot = await fetchItem(to.params.id)
        if (spot) {
            next(vm => {
                vm.item = spot
                vm.$nextTick(() => {
                    vm.initMap({
                        center: { lat: spot.coords.latitude, lng: spot.coords.longitude },
                        draggable: false,
                        logoControl: false,
                        mapDataControl: false,
                        minZoom: 11,
                        maxZoom: 11,
                        scaleControl: false,
                        scrollWheel: false
                    })
                })
            })
        }
        else {
            alert('존재하지 않는 공간입니다.')
        }
    },
    async beforeRouteUpdate(to, _, next) {
        const spot = await fetchItem(to.params.id)
        if (spot) {
            this.item = spot
            const coords = { lat: spot.coords.latitude, lng: spot.coords.longitude }
            this.map.setCenter(coords)
            this.marker.setPosition(coords)
            next()
        }
        else {
            alert('존재하지 않는 공간입니다.')
        }
    }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_parts';

.header {
    font-weight: 800;
    line-height: em(48);
    position: relative;
    text-align: center;
}
.back {
    position: absolute;
    top: 0;
    left: em(20);
}

.contents {
    padding-bottom: 8em;
}

.images {
    @include rowpad();
}
.images__dots {
    margin-top: em(12);
}
.image {
    width: 100%;
    display: block;
}

.titles {
    color: #fff;
    margin-top: em(24);
}
.type {
    font-family: 'Fugaz One', sans-serif;
    font-size: em(18);
    text-transform: uppercase;
    @include g-text-shadow();
}
.name {
    font-size: em(32);
    font-weight: 800;
    margin-top: em(6, 32);
    margin-right: em(-20, 32);
    word-break: keep-all;
    @include media(640) {
        margin-right: 0;
    }
}
.name__mask {
    padding-right: em(24, 32);
}
.oneliner {
    margin-top: em(20);
}

.titles__coupon {
    font-size: em(12);
    line-height: em(15, 12);
    margin-top: em(20, 12);
}
.titles__coupon-icon {
    width: em(15, 12);
    display: block;
    float: left;
    margin-right: em(6, 12);
}
.titles__coupon-cond {
    font-weight: 300;
    margin-right: .5ch;
}
.titles__coupon-discount {
    font-weight: 800;
}
.titles__coupon-text {
    font-weight: 300;
    line-height: 1.5;
    margin-left: em(21, 12);

    strong {
        font-weight: 800;
    }
}

.body {
    font-size: em(14);
    margin-top: em(52, 14);
}
.body__mobile {
    display: block;
    @include display-640(none)
}
.body__desktop {
    display: none;
    @include display-640(block)
}
.body__heading {
    color: #fff;
    font-weight: 800;
    @include media(640) {
        @include line(transparentize(#fff, .85))
    }
}
.body__text {
    line-height: em(24, 14);
    margin-top: 1em;
    margin-bottom: em(36, 14);
    @include media(640) {
        margin-bottom: em(45, 14);
    }
}

.map {
    margin-top: em(14);
    position: relative;
    @include rowpad();
    /deep/ img[src='http://static.naver.net/maps/mantle/2x/marker-default.png'] {
        z-index: 999;
    }
}
.map__body {
    width: 100%;
    height: em(180);
}
.map__pin {
    width: 22px;
    margin: auto;
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    transform: translateY(calc(-100% - 12px));
}
.map__caption {
    font-size: em(12);
    line-height: 2;
    margin-top: .5em;
    opacity: .8;
    text-align: center;
}

.title-mask {
    padding: 0 2em;
}
.coupon {
    margin-top: em(68);
}
.coupon__name {
    font-weight: 700;
    line-height: 1.5;
    margin-top: em(40);
    text-align: center;
}
.coupon__text {
    font-size: em(14);
    font-weight: 300;
    line-height: 1.5;
    margin-top: em(8, 14);
    text-align: center;
}
.coupon__cond {
    margin-right: .6ch;
}
.coupon__discount {
    font-weight: 700;
}
.coupon__image {
    width: 100%;
    max-width: em(335);
    display: block;
    margin: 2em auto 0;
}
.coupon__image-caption {
    font-size: em(12);
    line-height: em(20, 12);
    margin-top: em(18);
    opacity: .8;
    text-align: center;
}

.spot-programs {
    margin-top: em(72);
}
</style>